import React, { Component, CSSProperties } from 'react';
import cx from 'classnames';
import Sticky from 'react-stickynode';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import type { UserContext } from '../../../../actions/UserActions.type';

import UnifiedNavHtmlLoader from '../UnifiedNavHtmlLoader';

import initNavData from './initNavData';

// make sure this website nav gets loaded in a <StickyContainer/>

const getNavHeight = (isGuest: boolean, disableSecondary?: boolean): number => {
  if (isDesktop()) {
    const hegihtWithSecondary = isGuest ? 172 : 140;
    const heightWithoutSecondary = isGuest ? 136 : 96;
    return disableSecondary ? heightWithoutSecondary : hegihtWithSecondary;
  }
  return 60;
};

type UnifiedNavV2Props = {
  isHidden?: boolean;
  activePrimaryLink?: string;
  activeSecondaryLink?: string;
  user?: UserContext;
  fetchUserContext: () => Promise<void>;
  fetchNav: () => Promise<string>;
  styleOverride: {
    height: number;
    backgroundColor: string;
    zIndex: number;
  };
  fetchCategories: () => Promise<void>;
  fetchWeddingShopCategories: () => Promise<void>;
  showPromo: boolean;
  disablePrimaryNavCollapse: boolean;
  disableSecondary: boolean;
  isWeddingBoutiquePage: boolean;
  className: string;
};

type UnifiedNavV2State = {
  nav: string;
  height: number;
};

class UnifiedNavV2 extends Component<UnifiedNavV2Props, UnifiedNavV2State> {
  static defaultProps = {
    user: null,
    showPromo: false,
    disablePrimaryNavCollapse: false,
    disableSecondary: false,
  };

  constructor(props: UnifiedNavV2Props) {
    super(props);
    const isGuest = props.user ? props.user.is_guest : true;
    this.state = {
      nav: '',
      height: getNavHeight(isGuest, props.disableSecondary),
    };

    this.refreshNavHeight = _debounce(this.refreshNavHeight.bind(this), 200);
    this.handleInitNavData = this.handleInitNavData.bind(this);
  }

  componentDidMount(): void {
    const { fetchNav } = this.props;

    window.addEventListener('NAV_LOADED', this.handleInitNavData);
    window.addEventListener('resize', this.refreshNavHeight);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchNav().then((nav: string): void => {
      this.setState({ nav });
    });
  }

  componentDidUpdate(prevProps: UnifiedNavV2Props): void {
    const { user } = this.props;
    const { user: prevUser } = prevProps;
    if (!_isEqual(user, prevUser) && user) {
      this.refreshNavHeight();
      this.handleInitNavData();
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.refreshNavHeight);
    window.removeEventListener('NAV_LOADED', this.handleInitNavData);
  }

  handleInitNavData(): void {
    const {
      user,
      showPromo,
      fetchUserContext,
      fetchCategories,
      fetchWeddingShopCategories,
      activePrimaryLink,
      activeSecondaryLink,
      disablePrimaryNavCollapse,
      disableSecondary,
      isWeddingBoutiquePage,
    } = this.props;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initNavData({
      user,
      showPromo,
      fetchUserContext,
      fetchCategories,
      fetchWeddingShopCategories,
      activePrimaryLink,
      activeSecondaryLink,
      disablePrimaryNavCollapse,
      disableSecondary,
      isWeddingBoutiquePage,
    });
  }

  refreshNavHeight(): void {
    const { height } = this.state;
    const { disableSecondary, user } = this.props;
    const isGuest = user ? user.is_guest : true;
    const newHeight = getNavHeight(isGuest, disableSecondary);
    if (height !== newHeight) {
      this.setState({ height: newHeight });
    }
  }

  render(): JSX.Element {
    const { styleOverride, className, isHidden } = this.props;
    const hiddenStyles: CSSProperties = isHidden ? { position: 'absolute', left: '-99999px' } : {};
    const { nav, height } = this.state;
    const defaultStyles = {
      zIndex: 500,
      height,
      transform: 'none',
    };
    return (
      <div
        className={cx('unified-nav', className)}
        style={{ ...defaultStyles, ...styleOverride, ...hiddenStyles }}
      >
        <Sticky innerZ={500} enableTransforms={false}>
          <UnifiedNavHtmlLoader html={nav} />
        </Sticky>
      </div>
    );
  }
}

export default UnifiedNavV2;
